import type { MeasurementStatus, Severity } from '../types/common.js';
import type { CheckResult, CheckRunContext, CheckRequirement } from '../types/checks.js';
import { resolveBounds } from '../types/checks.js';
import type { MeasurementSpec, OfficialBounds, PolicySpec, ResolvedRule } from '../types/profile.js';
import { roundTo } from '../types/geometry.js';

/**
 * Shared helpers for the individual checks.
 *
 * Keeping status logic here is what makes the checks small, uniform and
 * testable, and it is also where the official/policy/measurement separation is
 * enforced:
 *
 *   official     — the authority's own bounds;
 *   measurement  — our uncertainty (a value inside the tolerance band is never
 *                  reported as a hard failure);
 *   policy       — an internal product bound; violating it never becomes an
 *                  "official" failure (it is reported as a warning by default).
 */

export interface JudgeRangeInput {
  value: number;
  min?: number;
  max?: number;
  /** Share of the allowed span that counts as "close to the bound". */
  warningMargin?: number;
  /** 0..1 confidence of the underlying measurement. */
  confidence?: number;
  /** Below this confidence the verdict degrades to `uncertain`. */
  uncertainBelow?: number;
  /** Share of the allowed window that counts as "comfortably inside". */
  lowConfidenceComfort?: number;
}

export interface JudgeResult {
  status: MeasurementStatus;
  /** True when the value is inside [min, max]. */
  inside: boolean;
  /** Machine-readable explanation of a non-pass status. */
  reason?:
    | 'below_min'
    | 'above_max'
    | 'low_confidence'
    | 'value_missing'
    | 'near_bound'
    | 'within_tolerance'
    | 'policy_below_min'
    | 'policy_above_max';
  /** True when the violation comes from an internal policy bound. */
  policyViolation?: boolean;
}

const isComfortable = (
  value: number,
  min: number | undefined,
  max: number | undefined,
  comfort: number,
): boolean => {
  if (min !== undefined && max !== undefined && max > min) {
    const span = max - min;
    return value >= min + span * (1 - comfort) * 0.5 && value <= max - span * (1 - comfort) * 0.5;
  }
  if (max !== undefined) return value <= max * comfort;
  if (min !== undefined) return value >= min * (1 + comfort);
  return true;
};

/** Legacy range judge (flat bounds). Kept for checks without official metadata. */
export const judgeRange = (input: JudgeRangeInput): JudgeResult => {
  const { value, min, max } = input;
  if (!Number.isFinite(value)) {
    return { status: 'uncertain', inside: false, reason: 'value_missing' };
  }

  const insideMin = min === undefined || value >= min;
  const insideMax = max === undefined || value <= max;
  const inside = insideMin && insideMax;

  const confidence = input.confidence;
  const uncertainBelow = input.uncertainBelow ?? 0;
  if (confidence !== undefined && confidence < uncertainBelow) {
    if (!isComfortable(value, min, max, input.lowConfidenceComfort ?? 0.5)) {
      return { status: 'uncertain', inside, reason: 'low_confidence' };
    }
  }

  if (!inside) {
    return { status: 'fail', inside: false, reason: insideMin ? 'above_max' : 'below_min' };
  }

  const margin = input.warningMargin ?? 0.08;
  if (min !== undefined && max !== undefined && max > min) {
    const span = max - min;
    const epsilon = span * 1e-6;
    const nearMin = value - min > epsilon && value - min < span * margin;
    const nearMax = max - value > epsilon && max - value < span * margin;
    if (nearMin || nearMax) {
      return { status: 'warning', inside: true, reason: 'near_bound' };
    }
  }

  return { status: 'pass', inside: true };
};

export interface JudgeBoundsInput {
  value: number;
  official: OfficialBounds;
  effective: OfficialBounds;
  measurement: MeasurementSpec;
  policy?: PolicySpec;
  confidence?: number;
  /** Share of the official span treated as a soft warning band. */
  warningMargin?: number;
  lowConfidenceComfort?: number;
}

/**
 * Judges a measurement against official bounds, the measurement tolerance and
 * an optional internal policy, keeping the three apart in the verdict.
 */
export const judgeBounds = (input: JudgeBoundsInput): JudgeResult => {
  const { value, official, effective, measurement, policy } = input;
  if (!Number.isFinite(value)) {
    return { status: 'uncertain', inside: false, reason: 'value_missing' };
  }

  const tolerance = Math.max(0, measurement.tolerance ?? 0);
  const confidence = input.confidence;
  const uncertainBelow = measurement.uncertainBelowConfidence ?? 0;

  const insideOfficial =
    (official.min === undefined || value >= official.min - tolerance) &&
    (official.max === undefined || value <= official.max + tolerance);
  const insideEffective =
    (effective.min === undefined || value >= effective.min) &&
    (effective.max === undefined || value <= effective.max);

  // Low confidence only permits a pass when the value is comfortably inside.
  if (confidence !== undefined && confidence < uncertainBelow) {
    if (!isComfortable(value, effective.min, effective.max, input.lowConfidenceComfort ?? 0.5)) {
      return { status: 'uncertain', inside: insideEffective, reason: 'low_confidence' };
    }
  }

  const policyMinActive =
    effective.min !== undefined &&
    (official.min === undefined || effective.min > official.min) &&
    policy?.min !== undefined;
  const policyMaxActive =
    effective.max !== undefined &&
    (official.max === undefined || effective.max < official.max) &&
    policy?.max !== undefined;

  if (effective.min !== undefined && value < effective.min) {
    if (policyMinActive) {
      const status: MeasurementStatus = policy?.onViolation === 'error' ? 'fail' : 'warning';
      return { status, inside: false, reason: 'policy_below_min', policyViolation: true };
    }
    // Outside the official minimum: only a hard failure beyond the tolerance.
    if (official.min !== undefined && value >= official.min - tolerance) {
      return { status: 'uncertain', inside: false, reason: 'within_tolerance' };
    }
    return { status: 'fail', inside: false, reason: 'below_min' };
  }

  if (effective.max !== undefined && value > effective.max) {
    if (policyMaxActive) {
      const status: MeasurementStatus = policy?.onViolation === 'error' ? 'fail' : 'warning';
      return { status, inside: false, reason: 'policy_above_max', policyViolation: true };
    }
    if (official.max !== undefined && value <= official.max + tolerance) {
      return { status: 'uncertain', inside: false, reason: 'within_tolerance' };
    }
    return { status: 'fail', inside: false, reason: 'above_max' };
  }

  if (!insideOfficial) {
    // Inside the enforced window but outside the official one (tolerance band).
    return { status: 'uncertain', inside: false, reason: 'within_tolerance' };
  }

  const warningMargin = input.warningMargin ?? 0.08;
  if (official.min !== undefined && official.max !== undefined && official.max > official.min) {
    const span = official.max - official.min;
    const epsilon = span * 1e-6;
    const nearMin = value - official.min > epsilon && value - official.min < span * warningMargin;
    const nearMax = official.max - value > epsilon && official.max - value < span * warningMargin;
    if (nearMin || nearMax) {
      return { status: 'warning', inside: true, reason: 'near_bound' };
    }
  }

  return { status: 'pass', inside: true };
};

/** Convenience wrapper: resolves the rule's bounds and judges the value. */
export const judgeRule = (
  rule: ResolvedRule,
  value: number,
  options: { confidence?: number; warningMargin?: number; lowConfidenceComfort?: number } = {},
): {
  verdict: JudgeResult;
  bounds: ReturnType<typeof resolveBounds>;
} => {
  const bounds = resolveBounds(rule);
  return {
    bounds,
    verdict: judgeBounds({
      value,
      official: bounds.official,
      effective: bounds.effective,
      measurement: bounds.measurement,
      ...(bounds.policy ? { policy: bounds.policy } : {}),
      ...(options.confidence !== undefined ? { confidence: options.confidence } : {}),
      ...(options.warningMargin !== undefined ? { warningMargin: options.warningMargin } : {}),
      ...(options.lowConfidenceComfort !== undefined
        ? { lowConfidenceComfort: options.lowConfidenceComfort }
        : {}),
    }),
  };
};

/** Human reason when a required capability is not available. */
export const missingRequirementReason = (
  ctx: CheckRunContext,
  requires: readonly CheckRequirement[],
): string | null => {
  for (const requirement of requires) {
    switch (requirement) {
      case 'pixels':
        if (!ctx.pixels) {
          return 'Пиксельный анализ недоступен: изображение не было декодировано в браузере.';
        }
        break;
      case 'faces':
        if (ctx.faces.source === 'unknown' && ctx.faces.faces.length === 0) {
          return 'Детектор лиц недоступен: модель анализа лица не загрузилась в браузере.';
        }
        break;
      case 'geometry':
        if (!ctx.geometry) {
          return 'Геометрия лица не рассчитана: не удалось получить landmarks.';
        }
        break;
      case 'metadata':
        if (!ctx.fileInfo.metadata) {
          return 'Метаданные файла недоступны: байты изображения не были прочитаны.';
        }
        break;
      case 'quality':
        if (!ctx.quality) {
          return 'Метрики качества не рассчитаны (глубокий анализ не выполнялся).';
        }
        break;
      case 'background':
        if (!ctx.background) {
          return 'Анализ фона не выполнялся или невозможен для этого кадра.';
        }
        break;
      default:
        break;
    }
  }
  return null;
};

export const notChecked = (
  _ctx: CheckRunContext,
  check: { id: string; title: string },
  message: string,
  severity: Severity,
): CheckResult => ({
  id: check.id,
  title: check.title,
  status: 'not_checked',
  severity,
  message,
  fixability: 'none',
});

/** Formats a normalized ratio as a percentage string for the UI. */
export const asPercent = (value: number, digits = 1): string =>
  `${roundTo(value * 100, digits).toFixed(digits).replace('.', ',')} %`;

export const asNumber = (value: number, unit: string, digits = 1): string =>
  `${roundTo(value, digits).toFixed(digits).replace('.', ',')} ${unit}`;

/** Result of a technical check that has no geometry to draw. */
export const plainResult = (result: CheckResult): CheckResult => result;

export const frameGeometry = (): CheckResult['geometry'] => [
  { type: 'rect', coordinates: [0, 0, 1, 1] },
];

/** Human description of official bounds, e.g. «50–69 %». */
export const describeOfficial = (
  official: OfficialBounds,
  options: { unit?: string; reference?: string } = {},
): string | undefined => {
  const unit = official.unit ?? options.unit;
  const format = (value: number): string =>
    unit === 'ratio' || unit === '%' ? `${roundTo(value * 100, 0)} %` : String(value);
  if (official.min !== undefined && official.max !== undefined) {
    return `${format(official.min)}–${format(official.max)}`;
  }
  if (official.max !== undefined) return `не более ${format(official.max)}`;
  if (official.min !== undefined) return `не менее ${format(official.min)}`;
  return undefined;
};

/** Attaches the official/measurement/policy blocks to a result. */
export const withRequirement = (
  result: CheckResult,
  rule: ResolvedRule,
  extra: { reference?: string; unit?: string; tolerance?: number } = {},
): CheckResult => {
  const bounds = resolveBounds(rule);
  const unit = extra.unit ?? bounds.official.unit;
  const reference = extra.reference ?? bounds.official.reference;
  const existingDetails = result.details ?? {};
  /**
   * A check may declare that the profile's policy bounds are expressed on a
   * different scale than its own value (e.g. a legacy Laplacian threshold of
   * 110 while the check now measures a 0…1 score). Such a bound must not be
   * attached at all: comparing the two scales would invent a policy violation
   * and could turn a passing photo into a hard failure.
   */
  const policyApplies = existingDetails['policyApplies'] !== false;
  /**
   * Infer a policy violation centrally: if the profile declares a numeric
   * policy bound and the measured value crosses it, the finding came from our
   * own policy, not from the document. Checks that judge with `judgeRule`
   * already set the flag explicitly.
   */
  const policyViolated = (() => {
    if (!bounds.policy || !policyApplies) return undefined;
    const { min, max } = bounds.policy;
    if (min === undefined && max === undefined) return undefined;
    const value = result.value;
    if (value === undefined || !Number.isFinite(value)) return undefined;
    return (min !== undefined && value < min) || (max !== undefined && value > max);
  })();
  const policyViolation =
    existingDetails['policyViolation'] === true ? true : policyViolated === true;

  return {
    ...result,
    // A rule may state the requirement qualitatively (`official.statement`)
    // without numeric bounds; use it when the check did not write its own text.
    ...(result.requirement === undefined && bounds.official.statement
      ? { requirement: bounds.official.statement }
      : {}),
    ...(policyViolation ? { details: { ...existingDetails, policyViolation: true } } : {}),
    ...(Object.keys(bounds.official).length > 0 ? { official: bounds.official } : {}),
    measurement: {
      ...(result.value !== undefined ? { value: result.value } : {}),
      ...(unit ? { unit } : {}),
      ...(reference ? { reference } : {}),
      tolerance: extra.tolerance ?? bounds.measurement.tolerance ?? 0,
    },
    ...(bounds.policy && policyApplies ? { policy: bounds.policy } : {}),
  };
};
